import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart } from 'lucide-react';
import {
  fetchProductById,
  fetchProductVariants,
  fetchProducts,
  fetchProductsByCategory,
  fetchProductReviews,
} from '../utils/apiHelpers';
import { useCartStore } from '../store/useCartStore';
import { useWishlistStore } from '../store/useWishlistStore';
import { useToast } from '../context/ToastContext';
import { getImageUrl } from '../utils/imageHelper';
import {
  isPromotionActive,
  calculateDiscountedPrice,
} from '../utils/promotion';
import type { Product, Variant, Review } from '../utils/apiHelpers';
import ProductGrid from '../components/ProductGrid';

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const thumbScrollRef = useRef<HTMLDivElement>(null);
  const mainImageRef = useRef<HTMLDivElement>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [related, setRelated] = useState<Product[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  /** Selected specification options (color, size, and any custom variant attributes) */
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const addItem = useCartStore((s) => s.addItem);
  const { addItem: addWishlist, removeItem: removeWishlist, isWishlisted } =
    useWishlistStore();
  const { showToast } = useToast();
  const wishlisted = id ? isWishlisted(id) : false;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchProductById(Number(id)),
      fetchProductVariants(Number(id)),
    ])
      .then(([productData, variantData]) => {
        if (cancelled) return;
        console.log('Product Data:', productData);
        console.log('Vendor Name:', productData?.vendor_name);
        console.log('Vendor ID:', (productData.extra as any)?.vendor);
        console.log('Vendor Details:', (productData as any)?.extra?.vendor_details);
        console.log('Store Name:', (productData as any)?.extra?.vendor_details?.store_name);
        console.log('Additional Images:', (productData as any)?.extra?.additional_images);
        setProduct(productData);
        setVariants(variantData);
        if (variantData.length > 0) {
          const first = variantData[0];
          const initial: Record<string, string> = {};
          if (first.color) initial.color = first.color;
          if (first.size) initial.size = first.size;
          Object.entries(first.customAttributes || {}).forEach(([k, v]) => {
            if (v != null && String(v).trim() !== '') initial[k] = String(v);
          });
          setSelectedOptions(initial);
        }
      })
      .catch(() => {
        if (!cancelled) setProduct(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Load related products (same category) and reviews once product is available
  useEffect(() => {
    if (!product) return;
    let cancelled = false;

    const relatedCategoryId = product.categoryId ??
      (typeof product.extra?.category === 'number' ? product.extra.category : undefined);

    if (relatedCategoryId != null) {
      setLoadingRelated(true);
      fetchProductsByCategory(Number(relatedCategoryId), true, 8)
        .then((list) => {
          if (cancelled) return;
          setRelated(list.filter((p) => p.id !== product.id));
        })
        .catch(() => {
          if (!cancelled) setRelated([]);
        })
        .finally(() => {
          if (!cancelled) setLoadingRelated(false);
        });
    } else {
      const categorySlug =
        (product.extra?.category as string | undefined) ?? undefined;
      if (categorySlug) {
        setLoadingRelated(true);
        fetchProducts({ category: categorySlug, page_size: 8 })
          .then((list) => {
            if (cancelled) return;
            setRelated(list.filter((p) => p.id !== product.id));
          })
          .catch(() => {
            if (!cancelled) setRelated([]);
          })
          .finally(() => {
            if (!cancelled) setLoadingRelated(false);
          });
      } else {
        setRelated([]);
      }
    }

    // Reviews for this product
    setLoadingReviews(true);
    fetchProductReviews(product.id)
      .then((list) => {
        if (!cancelled) setReviews(list);
      })
      .catch(() => {
        if (!cancelled) setReviews([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingReviews(false);
      });

    return () => {
      cancelled = true;
    };
  }, [product]);

  /** Variation options: keys and values from variants only (color, size, customAttributes) */
  const { optionKeys, optionValues } = useMemo(() => {
    const keys = new Set<string>();
    const values: Record<string, string[]> = {};
    variants.forEach((v) => {
      if (v.color) keys.add('color');
      if (v.size) keys.add('size');
      Object.keys(v.customAttributes || {}).forEach((k) => keys.add(k));
    });
    const keyList = Array.from(keys);
    keyList.forEach((key) => {
      const set = new Set<string>();
      variants.forEach((v) => {
        let val: string | undefined;
        if (key === 'color') val = v.color;
        else if (key === 'size') val = v.size;
        else val = v.customAttributes?.[key] != null ? String(v.customAttributes[key]) : undefined;
        if (val != null && val.trim() !== '') set.add(val);
      });
      values[key] = Array.from(set);
    });
    const finalKeyList = keyList.filter((key) => (values[key] || []).length > 0);
    return { optionKeys: finalKeyList, optionValues: values };
  }, [variants]);

  /** Specification entries from product (read-only); show when any has displayable value */
  const hasSpecification = product?.specification && Object.keys(product.specification).length > 0;
  const specificationEntries = useMemo(() => {
    if (!product?.specification) return [];
    return Object.entries(product.specification).filter(([key, v]) => {
      const normalizedKey = String(key).toLowerCase();
      if (
        normalizedKey === 'created_at' ||
        normalizedKey === 'updated_at' ||
        normalizedKey === 'id' ||
        normalizedKey === 'product_id'
      ) {
        return false;
      }
      return v != null && v !== '' && (typeof v !== 'object' || (Array.isArray(v) && v.length > 0));
    });
  }, [product?.specification]);
  const showSpecification = hasSpecification && specificationEntries.length > 0;

  const normalizeSpecificationValue = (value: unknown): unknown => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return value;
        }
      }
    }
    return value;
  };

  const truncateWords = (text: string, maxWords = 15) => {
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return `${words.slice(0, maxWords).join(' ')}...`;
  };

  const formatSpecificationValue = (value: unknown): string => {
    const normalized = normalizeSpecificationValue(value);
    if (Array.isArray(normalized)) {
      return normalized
        .map((item) => formatSpecificationValue(item))
        .filter(Boolean)
        .join(', ');
    }
    if (normalized && typeof normalized === 'object') {
      return Object.entries(normalized)
        .map(([key, item]) => `${key}: ${formatSpecificationValue(item)}`)
        .join(', ');
    }
    return String(normalized ?? '');
  };

  const formatSpecificationValueForKey = (key: string, value: unknown): string => {
    const normalizedKey = String(key).toLowerCase();
    const formatted = formatSpecificationValue(value);
    if (
      normalizedKey === 'name' ||
      normalizedKey === 'product_name' ||
      normalizedKey.endsWith('_name') ||
      normalizedKey === 'title'
    ) {
      return formatted;
    }
    return truncateWords(formatted, 15);
  };

  const selectedVariant = useMemo(() => {
    if (!variants.length || !optionKeys.length) return undefined;
    return variants.find((v) =>
      optionKeys.every((key) => {
        const selected = selectedOptions[key];
        if (!selected) return true;
        if (key === 'color') return v.color === selected;
        if (key === 'size') return v.size === selected;
        return String(v.customAttributes?.[key] ?? '') === selected;
      })
    );
  }, [variants, optionKeys, selectedOptions]);

  if (loading) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse h-96 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </main>
    );
  }

  if (!product) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-gray-500">Product not found.</p>
        <Link to="/shop" className="text-green-600 hover:underline mt-2 inline-block">
          Back to shop
        </Link>
      </main>
    );
  }

  const images = product.images?.length
    ? product.images.map((i) => i.url)
    : (() => {
        const imageList: string[] = [];
        // Always add main product image first
        if (product.product_image) {
          imageList.push(product.product_image);
        }
        // Then add additional images
        if (Array.isArray(product.extra?.additional_images) && product.extra.additional_images.length) {
          const additionalImages = (product.extra.additional_images as any[]).map((img: any) => img.image);
          imageList.push(...additionalImages);
        }
        return imageList;
      })();
  const mainImage = images[selectedImage] || product.product_image;
  const hasPromo = isPromotionActive(product);
  const price = parseFloat(product.price);
  const baseDisplayPrice =
    hasPromo && product.promotion
      ? calculateDiscountedPrice(price, product.promotion)
      : price;
  const displayPrice = selectedVariant != null ? selectedVariant.price : baseDisplayPrice;
  const displayStock = selectedVariant != null ? selectedVariant.stock : (product.stock ?? 0);

  const addCurrentToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: displayPrice,
      image: getImageUrl(selectedVariant?.image || product.product_image) || '',
      quantity,
      variant_id: selectedVariant?.id ?? null,
      specification: { ...selectedOptions },
    });
  };

  const handleAddToCart = () => {
    if (displayStock <= 0) {
      showToast('This product is out of stock', 'error');
      return;
    }
    addCurrentToCart();
    showToast('Product added to cart!', 'success');
  };

  const handleBuyNow = () => {
    if (displayStock <= 0) {
      showToast('This product is out of stock', 'error');
      return;
    }
    addCurrentToCart();
    showToast('Redirecting to checkout', 'success');
    navigate('/checkout');
  };

  

  const toggleWishlist = () => {
    if (wishlisted) {
      removeWishlist(product.id);
      showToast('Removed from wishlist', 'info');
    } else {
      addWishlist({
        id: product.id,
        name: product.name,
        price: product.price,
        image: getImageUrl(product.product_image) || '',
        addedAt: Date.now(),
      });
      showToast('Added to wishlist', 'success');
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mainImageRef.current) return;
    const rect = mainImageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePos({ x, y });
  };

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  const handleVendorClick = () => {
    if (!product) return;
    
    console.log('Vendor button clicked');
    console.log('Product object:', product);
    console.log('Extra object:', (product as any).extra);
    
    const vendorDetails = (product.extra as any)?.vendor_details;
    let vendorId = vendorDetails?.id || (product.extra as any)?.vendor || (product as any)?.vendor_id;
    
    console.log('Extracted vendor ID:', vendorId);
    
    if (vendorId) {
      // Store vendor info in localStorage if available
      if (vendorDetails) {
        localStorage.setItem(
          `vendor_${vendorId}`,
          JSON.stringify(vendorDetails)
        );
      }
      // Navigate to vendor page
      console.log('Navigating to /vendor/' + vendorId);
      navigate(`/vendor/${vendorId}`);
    } else {
      console.warn('No vendor ID found in product data');
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <nav className="text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-1">/</span>
        <Link to="/shop" className="hover:underline">Shop</Link>
        <span className="mx-1">/</span>
        <span>{product.category_name || 'Category'}</span>
        <span className="mx-1">/</span>
        <span className="text-gray-700 truncate max-w-[180px] inline-block align-bottom" title={product.name}>
          {product.name}
        </span>
      </nav>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-2xl p-4">
          <div className="flex flex-col gap-4">
            <div
              ref={mainImageRef}
              className="flex items-center justify-center min-h-[280px] overflow-hidden rounded-lg"
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <img
                src={getImageUrl(mainImage)}
                alt={product.name}
                className="w-full max-h-[420px] object-fill transition-transform duration-100"
                style={{
                  transformOrigin: `${mousePos.x}% ${mousePos.y}%`,
                  transform: isHovering ? 'scale(1.5)' : 'scale(1)',
                }}
              />
            </div>
            {/* Horizontal thumbnail carousel below main image */}
            <div
              ref={thumbScrollRef}
              className="flex gap-2 overflow-x-auto overflow-y-hidden scrollbar-hide py-1"
            >
              {images.map((url, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedImage(i)}
                  className={`w-16 h-16 rounded-lg border-2 overflow-hidden bg-gray-50 flex-shrink-0 flex items-center justify-center ${
                    selectedImage === i ? 'border-[#fd9e10]' : 'border-gray-200'
                  }`}
                >
                  <img
                    src={getImageUrl(url)}
                    alt=""
                    className="w-full h-full object-fill"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl p-6">
          <p className="text-[11px] tracking-widest text-orange-500 font-semibold uppercase">
            {product.category_name || 'Category'}
          </p>
        
        
        
        
          <h2 className="text-2xl font-bold text-[var(--foreground)] mt-2">
            {product.name}
          </h2>

          <div className="flex items-center gap-3 mt-4">
            <span className="text-2xl font-bold text-orange-600">
              Rs {displayPrice.toLocaleString()}
            </span>
            {product.base_price && parseFloat(product.base_price) > displayPrice && (
              <span className="text-gray-500 line-through">
                Rs {parseFloat(product.base_price).toLocaleString()}
              </span>
            )}
          </div>
          <p className="text-sm text-green-600 mt-2">
            {displayStock > 0
              ? `✔ ${displayStock} in stock | Fast delivery nationwide`
              : 'Out of stock'}
          </p>
          {(product.vendor_name || product.store_name) && (
            <p className="text-sm text-gray-700 mt-2">
              <span className="font-semibold">Vendor:</span> 
              <button
                type="button"
                onClick={handleVendorClick}
                className="text-orange-600 hover:underline ml-1 cursor-pointer font-medium"
              >
                {product.vendor_name || product.store_name}
              </button>
            </p>
          )}
          {product.description && (
            <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">
              {product.description}
            </p>
          )}

          {/* Variants + Specification: side-by-side grid on desktop */}
          {(optionKeys.length > 0 || showSpecification) && (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {optionKeys.length > 0 && (
                <div className="p-4 rounded-xl">
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-3">
                    SELECT OPTIONS
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {optionKeys.map((key) => {
                      const options = optionValues[key] || [];
                      const selected = selectedOptions[key];
                      const label =
                        key === 'color' ? 'COLOR' : key === 'size' ? 'SIZE' : key.toUpperCase();
                      if (key === 'color') {
                        return (
                          <div key={key}>
                            <p className="text-xs text-gray-500 mb-2">{label}</p>
                            <div className="flex gap-2">
                              {options.map((value) => (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() =>
                                    setSelectedOptions((prev) => 
                                      prev[key] === value
                                        ? { ...prev, [key]: '' }
                                        : { ...prev, [key]: value }
                                    )
                                  }
                                  className={`w-8 h-8 rounded-full border-2 flex-shrink-0 ${
                                    selected === value
                                      ? 'border-orange-500 ring-2 ring-orange-200'
                                      : 'border-gray-300'
                                  }`}
                                  title={value}
                                  style={{ backgroundColor: value.toLowerCase() }}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={key}>
                          <p className="text-xs text-gray-500 mb-2">{label}</p>
                          <div className="flex gap-2 flex-wrap">
                            {options.map((value) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() =>
                                  setSelectedOptions((prev) => 
                                    prev[key] === value
                                      ? { ...prev, [key]: '' }
                                      : { ...prev, [key]: value }
                                  )
                                }
                                className={`px-4 py-2 rounded border text-sm font-medium ${
                                  selected === value
                                    ? 'border-orange-500 text-orange-600 bg-orange-50'
                                    : 'border-gray-300 text-gray-600 bg-white'
                                }`}
                              >
                                {value || 'One'}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {showSpecification && (
                <div className="p-4 rounded-xl">
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-3">
                    SPECIFICATION
                  </p>
                  <div className="space-y-2">
                    {specificationEntries.map(([k, v]) => (
                      <div key={k} className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[40%]">
                          {k}
                        </p>
                        <p
                          className="text-sm text-gray-700 text-right flex-1 truncate whitespace-nowrap"
                          title={String(v)}
                        >
                          {formatSpecificationValueForKey(k, v)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-5">
            <label className="text-sm text-gray-600">Quantity</label>
            <div className="flex items-center border border-gray-300 rounded overflow-hidden">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="px-3 py-2 text-gray-600 hover:bg-gray-50"
              >
                -
              </button>
              <span className="px-4 py-2 text-sm border-x border-gray-300 min-w-[2.5rem] text-center">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
                className="px-3 py-2 text-gray-600 hover:bg-gray-50"
              >
                +
              </button>
            </div>
          </div>

          <div className="flex gap-3 mt-5 flex-wrap">
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={displayStock <= 0}
              className={`flex-1 min-w-[140px] py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
                displayStock <= 0
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-[#fd9e10] text-white hover:bg-[#f08c00]'
              }`}
            >
              <ShoppingCart className="h-5 w-5" />
              Add To Cart
            </button>
            <button
              type="button"
              onClick={handleBuyNow}
              disabled={displayStock <= 0}
              className={`flex-1 min-w-[140px] py-3 rounded-lg font-semibold ${
                displayStock <= 0
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-[#0b0320] text-white hover:opacity-90'
              }`}
            >
              Buy Now
            </button>
            <button
              type="button"
              onClick={toggleWishlist}
              className="p-3 border border-gray-300 rounded-lg hover:bg-gray-100"
              aria-label="Wishlist"
            >
              <Heart
                className="h-5 w-5"
                fill={wishlisted ? 'red' : 'none'}
                stroke={wishlisted ? 'red' : 'currentColor'}
              />
            </button>
          </div>

        </div>
      </div>

      <section className="mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-[var(--foreground)] mb-4">Product Details</h2>
            <div className="space-y-3 text-sm text-gray-700">
              {product.description ? (
                <p className="whitespace-pre-line">{product.description}</p>
              ) : (
                <p>No detailed description available.</p>
              )}
              <div className="grid gap-3">
                {product.category_name && (
                  <div className="flex justify-between border-t border-gray-100 pt-3">
                    <span className="text-gray-500">Category</span>
                    <span className="font-medium text-gray-900">{product.category_name}</span>
                  </div>
                )}
                {(product.vendor_name || product.store_name) && (
                  <div className="flex justify-between border-t border-gray-100 pt-3">
                    <span className="text-gray-500">Brand</span>
                    <span className="font-medium text-gray-900">{product.vendor_name || product.store_name}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-100 pt-3">
                  <span className="text-gray-500">Price</span>
                  <span className="font-medium text-gray-900">Rs {displayPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-t border-gray-100 pt-3">
                  <span className="text-gray-500">Stock</span>
                  <span className="font-medium text-gray-900">{displayStock > 0 ? `${displayStock} available` : 'Out of stock'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            {images.slice(0, 2).map((url, index) => (
              <div key={index} className="overflow-hidden rounded-3xl">
                <img
                  src={getImageUrl(url)}
                  alt={`${product.name} image ${index + 1}`}
                  className="h-72 w-full object-fill"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related products */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-[var(--foreground)] mb-4">
          Related Products
        </h2>
        <ProductGrid products={related} loading={loadingRelated} />
      </section>

      {/* Reviews */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-[var(--foreground)] mb-4">
          Customer Reviews
        </h2>
        {loadingReviews ? (
          <p className="text-sm text-gray-500">Loading reviews...</p>
        ) : reviews.length === 0 ? (
          <p className="text-sm text-gray-500">No reviews yet for this product.</p>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => (
              <div
                key={r.id}
                className="rounded-lg p-4"
              >
                <div className="mb-1">
                  <p className="text-sm font-semibold text-[var(--foreground)]">
                    {r.user_name}
                  </p>
                </div>
                <div className="flex items-center gap-1 mb-2">
                  <span className="text-xs font-medium text-orange-500">
                    {r.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-400">/ 5</span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line">
                  {r.comment || 'No comment provided.'}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
